import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Workflow, Block, WorkflowBlock, Position, Connection } from '../types';
import { api } from '../api';
import { PromptTestModal } from './PromptTestModal';
import DecisionBlockConfigPanel from './DecisionBlockConfigPanel';

export const WorkflowEditor: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const [workflow, setWorkflow] = React.useState<Workflow | null>(null);
  const [blocks, setBlocks] = React.useState<Block[]>([]);
  const [draggedBlock, setDraggedBlock] = React.useState<Block | null>(null);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [sourceBlock, setSourceBlock] = React.useState<WorkflowBlock | null>(null);
  const [tempConnection, setTempConnection] = React.useState<{ start: Position; end: Position } | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveTimeout, setSaveTimeout] = React.useState<NodeJS.Timeout | null>(null);
  
  // Clean up unused state and add a more reliable scale state
  const [scale, setScale] = React.useState<number>(1);
  const [panPosition, setPanPosition] = React.useState<Position>({ x: 0, y: 0 });
  
  // Zoom and pan state
  const [isPanning, setIsPanning] = React.useState(false);
  const [startPan, setStartPan] = React.useState({ x: 0, y: 0 });
  const [movingBlockId, setMovingBlockId] = React.useState<string | null>(null);
  const [initialBlockPos, setInitialBlockPos] = React.useState<Position | null>(null);
  const [initialMousePos, setInitialMousePos] = React.useState<Position | null>(null);

  // Add new state for hover state on blocks
  const [hoveredBlockId, setHoveredBlockId] = React.useState<string | null>(null);

  // State for creating connections
  const [mousePosition, setMousePosition] = React.useState<{x: number, y: number}>({x: 0, y: 0});

  // State for dragging blocks
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [draggedBlockId, setDraggedBlockId] = React.useState<string | null>(null);
  const dragOffsetRef = React.useRef<{x: number, y: number}>({x: 0, y: 0});
  
  // Get the container ref
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Add execution state
  const [isExecuting, setIsExecuting] = React.useState(false);
  const [executionId, setExecutionId] = React.useState<string | null>(null);
  const [executionStatus, setExecutionStatus] = React.useState<string | null>(null);

  // Add additional states for start and results text
  const [startText, setStartText] = React.useState<string>('');
  const [resultsText, setResultsText] = React.useState<string>('');
  const [executionResults, setExecutionResults] = React.useState<any>(null);
  const [forceUpdate, setForceUpdate] = React.useState(0); // Force re-render for connection alignment

  // State for decision block configuration modal
  const [configModalOpen, setConfigModalOpen] = React.useState(false);
  const [configuringBlock, setConfiguringBlock] = React.useState<WorkflowBlock | null>(null);
  
  // State for tracking which route is being connected from a decision block
  const [selectedRouteId, setSelectedRouteId] = React.useState<string | null>(null);

  // Add debounce ref for zoom operations
  const zoomTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastZoomTimeRef = React.useRef<number>(0);
  
  // Refs to track current scale and pan for smooth zoom calculations
  const scaleRef = React.useRef<number>(1);
  const panPositionRef = React.useRef<Position>({ x: 0, y: 0 });
  
  // Keep refs in sync with state
  React.useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);
  
  React.useEffect(() => {
    panPositionRef.current = panPosition;
  }, [panPosition]);

  // Add state for zoom transition
  const [isZooming, setIsZooming] = React.useState(false);

  // Add a ref for animation frame
  const requestAnimationFrameRef = React.useRef<number | null>(null);

  // Reference to store the currently dragged element
  const draggedElementRef = React.useRef<HTMLElement | null>(null);

  // Add window size state
  const [windowSize, setWindowSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Add a state to manage the test modal
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testBlockData, setTestBlockData] = useState<{id: string, blockId: string, name: string, prompt: string, model?: string}>({
    id: '',
    blockId: '',
    name: '',
    prompt: '',
    model: ''
  });

  // Add state for tracking block inputs and outputs during execution
  const [blockData, setBlockData] = React.useState<Record<string, { input: string, output: string, actualModel?: string }>>({});

  // Add state for category filtering and search
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(null);
  const [blockSearchText, setBlockSearchText] = React.useState<string>('');
  
  // Get unique categories from blocks
  const categories = React.useMemo(() => {
    const uniqueCategories = new Set(blocks.map(block => block.category));
    return Array.from(uniqueCategories).sort();
  }, [blocks]);
  
  // Filter blocks by selected category, search text, and sort Control blocks first
  const getFilteredBlocks = () => {
    let filtered = blocks;
    
    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(block => block.category === selectedCategory);
    }
    
    // Filter by search text
    if (blockSearchText) {
      const searchLower = blockSearchText.toLowerCase();
      filtered = filtered.filter(block => 
        block.name.toLowerCase().includes(searchLower) ||
        block.description.toLowerCase().includes(searchLower) ||
        block.category.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort: Control and Control Flow categories first, then alphabetically
    return filtered.sort((a, b) => {
      const priorityCategories = ['Control', 'Control Flow'];
      const builtInOrder = ['start-block', 'decision-router', 'results-block'];
      
      const aIsControl = priorityCategories.includes(a.category);
      const bIsControl = priorityCategories.includes(b.category);
      
      // If both are Control/Control Flow, sort by specific block order
      if (aIsControl && bIsControl) {
        const aIndex = builtInOrder.indexOf(a.id);
        const bIndex = builtInOrder.indexOf(b.id);
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.name.localeCompare(b.name);
      }
      
      // If only a is Control/Control Flow, a comes first
      if (aIsControl) return -1;
      // If only b is Control/Control Flow, b comes first
      if (bIsControl) return 1;
      
      // Otherwise, sort alphabetically
      return a.name.localeCompare(b.name);
    });
  };

  // Auto-layout blocks function
  const autoLayoutBlocks = () => {
    if (!workflow) return;
    
    const layoutBlocks = [...workflow.blocks];
    const spacing = 350; // Horizontal spacing
    const verticalSpacing = 150; // Vertical spacing
    
    // Simple left-to-right layout
    layoutBlocks.forEach((block, index) => {
      block.position = {
        x: 100 + (index % 3) * spacing,
        y: 100 + Math.floor(index / 3) * verticalSpacing
      };
    });
    
    setWorkflow({ ...workflow, blocks: layoutBlocks });
    saveWorkflow({ ...workflow, blocks: layoutBlocks });
  };

  // Add resize handler effect
  React.useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    
    // Call once to set initial size
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const loadWorkflow = React.useCallback(async () => {
    if (!workflowId) return;
    try {
      const data = await api.getWorkflow(workflowId);
      
      // Debug: Log connections loaded from server
      console.log('📥 Loaded workflow from server:', {
        id: data.id,
        connectionsCount: data.connections.length,
        connections: data.connections.map((c: Connection) => ({
          id: c.id,
          sourceId: c.sourceId,
          targetId: c.targetId,
          routeId: (c as any).routeId || 'MISSING'
        })),
        blocks: data.blocks.map((b: WorkflowBlock) => ({
          id: b.id,
          blockId: b.blockId,
          hasData: !!b.data,
          dataKeys: b.data ? Object.keys(b.data) : [],
          routes: b.blockId === 'decision-router' ? ((b as any).data?.routes || []).map((r: any) => r.id) : null
        }))
      });
      
      // Repair connections from Decision Router blocks that are missing routeId
      // This handles old connections created before the routeId fix
      // First, create a map of connections by their index for easy lookup
      const connectionMap = new Map<string, Connection>();
      data.connections.forEach((conn, index) => {
        connectionMap.set(conn.id, conn);
      });
      
      // Group connections by Decision Router source block
      const connectionsByDecisionRouter = new Map<string, {
        connections: Connection[];
        routeIds: string[];
      }>();
      
      data.connections.forEach((conn: Connection) => {
        const sourceBlock = data.blocks.find(b => b.id === conn.sourceId);
        if (sourceBlock && sourceBlock.blockId === 'decision-router') {
          const decisionData = (sourceBlock as any).data || {};
          const routes = decisionData.routes || [];
          
          if (!connectionsByDecisionRouter.has(conn.sourceId)) {
            connectionsByDecisionRouter.set(conn.sourceId, {
              connections: [],
              routeIds: routes.map((r: any) => r.id)
            });
          }
          connectionsByDecisionRouter.get(conn.sourceId)!.connections.push(conn);
        }
      });
      
      // Repair connections from each Decision Router sequentially
      connectionsByDecisionRouter.forEach((group, sourceId) => {
        const routes = group.routeIds;
        const connectionsNeedingRepair = group.connections.filter(c => !c.routeId);
        const connectionsWithRouteId = group.connections.filter(c => c.routeId);
        
        // Find which routes are already used
        const usedRouteIds = new Set(
          connectionsWithRouteId.map(c => c.routeId).filter(Boolean)
        );
        
        // Assign unused routes to connections that need repair
        connectionsNeedingRepair.forEach((conn, index) => {
          // Find next available route
          const availableRoute = routes.find((r: string) => !usedRouteIds.has(r)) || routes[0];
          
          if (availableRoute) {
            console.warn(`🔧 Repairing connection ${conn.id}: Assigning routeId=${availableRoute} from Decision Router ${sourceId}`);
            usedRouteIds.add(availableRoute);
            // Update the connection in the map
            const updatedConn = { ...conn, routeId: availableRoute };
            connectionMap.set(conn.id, updatedConn);
          } else {
            console.warn(`⚠️ Connection ${conn.id} from Decision Router ${sourceId} has no routeId and no routes available`);
          }
        });
      });
      
      // Convert map back to array
      const repairedConnections = data.connections.map(conn => 
        connectionMap.get(conn.id) || conn
      );
      
      // Update data with repaired connections
      const repairedData = {
        ...data,
        connections: repairedConnections
      };
      
      // If any connections were repaired, save the workflow immediately
      const wasRepaired = repairedConnections.some((conn: Connection, index: number) => 
        conn !== data.connections[index]
      );
      
      if (wasRepaired) {
        console.log('🔧 Repaired connections from Decision Router blocks, saving workflow...');
        // Save the repaired workflow
        await api.updateWorkflow(workflowId, repairedData);
        setWorkflow(repairedData);
      } else {
        setWorkflow(data);
      }
    } catch (err) {
      console.error('Failed to load workflow:', err);
    }
  }, [workflowId]);

  const loadBlocks = React.useCallback(async () => {
    try {
      const data = await api.getBlocks();
      setBlocks(data || []);
    } catch (err) {
      console.error('Failed to load blocks:', err);
    }
  }, []);

  React.useEffect(() => {
    loadWorkflow();
    loadBlocks();
  }, [loadWorkflow, loadBlocks]);

  // Update the saveWorkflow function to handle errors and improve feedback
  const saveWorkflow = React.useCallback(async (updatedWorkflow: Workflow) => {
    if (!workflowId || isSaving) return;
    
    // Clear any existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    // Set a new timeout to save after 1 second of inactivity
    const timeoutId = setTimeout(async () => {
      try {
        setIsSaving(true);
        
        // Debug: Log what we're sending to the server
        console.log('📤 Sending workflow to server:', {
          id: updatedWorkflow.id,
          connectionsCount: updatedWorkflow.connections.length,
          connections: updatedWorkflow.connections.map(c => ({
            id: c.id,
            sourceId: c.sourceId,
            targetId: c.targetId,
            routeId: (c as any).routeId || 'MISSING'
          }))
        });
        
        // Also log the full connection objects for debugging
        console.log('📤 Full connection objects being sent:', updatedWorkflow.connections);
        
        const savedWorkflow = await api.updateWorkflow(workflowId, updatedWorkflow);
        
        // Debug: Log what we received from the server
        console.log('📥 Received workflow from server:', {
          id: savedWorkflow.id,
          connectionsCount: savedWorkflow.connections.length,
          connections: savedWorkflow.connections.map((c: Connection) => ({
            id: c.id,
            sourceId: c.sourceId,
            targetId: c.targetId,
            routeId: (c as any).routeId || 'MISSING'
          }))
        });
        
        // Also log the full connection objects for debugging
        console.log('📥 Full connection objects received:', savedWorkflow.connections);
        
        // Check if routeId was lost
        const sentRouteIds = updatedWorkflow.connections.map(c => (c as any).routeId).filter(Boolean);
        const receivedRouteIds = savedWorkflow.connections.map((c: Connection) => (c as any).routeId).filter(Boolean);
        if (sentRouteIds.length !== receivedRouteIds.length) {
          console.error('❌ routeId LOST!', {
            sent: sentRouteIds.length,
            received: receivedRouteIds.length,
            sentIds: sentRouteIds,
            receivedIds: receivedRouteIds
          });
        }
        
        // Update the local workflow with the response from the server
        setWorkflow(savedWorkflow);
        setIsSaving(false);
        console.log('Workflow saved successfully');
      } catch (err) {
        console.error('Failed to save workflow:', err);
        setIsSaving(false);
        // Show error message to the user (you might want to use a toast or notification)
        alert(`Failed to save workflow: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }, 1000);
    
    setSaveTimeout(timeoutId);
  }, [workflowId, isSaving, saveTimeout]);

  // Update workflow in state and trigger save to database
  const updateWorkflow = React.useCallback((updatedWorkflow: Workflow) => {
    // Validate connections don't reference non-existent blocks
    const blockIds = updatedWorkflow.blocks.map(block => block.id);
    const validConnections = updatedWorkflow.connections.filter(
      conn => blockIds.includes(conn.sourceId) && blockIds.includes(conn.targetId)
    );
    
    // If any connections were filtered out, warn the user
    if (validConnections.length < updatedWorkflow.connections.length) {
      console.warn('Some connections were referencing non-existent blocks and have been removed');
    }
    
    // Debug: Log connections with routeId
    console.log('💾 Saving workflow with connections:', updatedWorkflow.connections.map(c => ({
      id: c.id,
      sourceId: c.sourceId,
      targetId: c.targetId,
      routeId: (c as any).routeId || 'MISSING'
    })));
    
    // Create a clean copy with valid connections
    const cleanWorkflow = {
      ...updatedWorkflow,
      connections: validConnections
    };
    
    // Update state and save to backend
    setWorkflow(cleanWorkflow);
    saveWorkflow(cleanWorkflow);
  }, [saveWorkflow]);

  const handleDragStart = (block: Block) => {
    setDraggedBlock(block);
  };

  const handleDragEnd = () => {
    setDraggedBlock(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!draggedBlock || !workflow) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const blockPosition: Position = {
      x: (e.clientX - rect.left + e.currentTarget.scrollLeft - panPosition.x) / scale,
      y: (e.clientY - rect.top + e.currentTarget.scrollTop - panPosition.y) / scale,
    };

    const newWorkflowBlock: WorkflowBlock = {
      id: `block-${Date.now()}`,
      blockId: draggedBlock.id,
      position: blockPosition,
    };

    const updatedWorkflow: Workflow = {
      ...workflow,
      blocks: [...workflow.blocks, newWorkflowBlock],
    };

    updateWorkflow(updatedWorkflow);
    handleDragEnd();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleBlockClick = (e: React.MouseEvent, block: WorkflowBlock) => {
    // If we're in connecting mode and this isn't the source block, complete the connection
    if (isConnecting && sourceBlock && sourceBlock.id !== block.id) {
      // Call the connection handler to complete the connection
      // This will handle all validation including route-specific connections for decision routers
      handleConnectionButtonClick(e, block);
    }
    
    // Handle other block click functionality here (selection, etc.)
  };

  const handleBlockDoubleClick = (e: React.MouseEvent, workflowBlock: WorkflowBlock) => {
    e.stopPropagation();
    const blockDef = blocks.find(b => b.id === workflowBlock.blockId);
    
    // Open configuration modal for decision router blocks
    if (blockDef && blockDef.id === 'decision-router') {
      setConfiguringBlock(workflowBlock);
      setConfigModalOpen(true);
    }
  };

  const handleSaveDecisionConfig = (config: Record<string, any>) => {
    if (!workflow || !configuringBlock) return;
    
    // Update the workflow block's configuration WITHOUT closing the modal
    const updatedBlocks = workflow.blocks.map(block => {
      if (block.id === configuringBlock.id) {
        return {
          ...block,
          // Store decision config in a way that can be retrieved later
          data: config
        };
      }
      return block;
    });
    
        const updatedWorkflow = {
          ...workflow,
      blocks: updatedBlocks
        };
    
    // Update the workflow but keep the modal open
        updateWorkflow(updatedWorkflow);
    
    // Also update the configuringBlock state so it has the latest config
    setConfiguringBlock({
      ...configuringBlock,
      data: config
    } as any);
  };

  // Function to check if adding a connection would create a cycle
  const wouldCreateCircularConnection = (sourceId: string, targetId: string): boolean => {
    if (!workflow) return false;
    
    // If there's already a direct connection from target to source, it would create a cycle
    const hasDirectCycle = workflow.connections.some(
      conn => conn.sourceId === targetId && conn.targetId === sourceId
    );
    if (hasDirectCycle) return true;
    
    // Check for indirect cycles using depth-first search
    const visited = new Set<string>();
    const checkCycle = (currentId: string): boolean => {
      if (currentId === sourceId) return true; // Found a cycle
      if (visited.has(currentId)) return false; // Already checked this path
      
      visited.add(currentId);
      
      // Find all connections where this block is the source
      const outgoingConnections = workflow.connections.filter(conn => conn.sourceId === currentId);
      
      // Check each connection recursively
      for (const conn of outgoingConnections) {
        if (checkCycle(conn.targetId)) return true;
      }
      
      return false;
    };
    
    // Start the cycle detection from the target block
    return checkCycle(targetId);
  };

  const handleBlockMouseDown = (e: React.MouseEvent, blockId: string, position: Position) => {
    // Don't start dragging if we're in connecting mode or if the event doesn't originate from the block itself
    if (isConnecting) {
      return;
    }
    
    if (e.button === 0) { // Left mouse button
      e.stopPropagation(); // Prevent canvas panning when dragging blocks
      
      // Start dragging immediately to improve perceived performance
      if (containerRef.current) {
        // Add dragging class to container for optimized rendering
        containerRef.current.classList.add('block-dragging');
        
        // Calculate offset from the mouse position to the block position
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = (e.clientX - rect.left + containerRef.current.scrollLeft - panPosition.x) / scale;
        const mouseY = (e.clientY - rect.top + containerRef.current.scrollTop - panPosition.y) / scale;
        
        // Store drag offset for later use
        dragOffsetRef.current = {
          x: position.x - mouseX,
          y: position.y - mouseY
        };
        
        // Store reference to the element we're dragging for direct DOM updates
        draggedElementRef.current = e.currentTarget as HTMLElement;
        if (draggedElementRef.current) {
          // Add special class for optimizations
          draggedElementRef.current.style.willChange = 'transform, left, top';
        }
        
        // Update state to reflect dragging
        setIsDragging(true);
        setDraggedBlockId(blockId);
      }
    }
  };

  const handleCanvasClick = () => {
    setIsConnecting(false);
    setSourceBlock(null);
    setTempConnection(null);
    setSelectedRouteId(null);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only initiate panning with left mouse button (button === 0)
    if (e.button === 0 && !isConnecting && !isDragging) {
      e.preventDefault(); // Prevent text selection during drag
      setIsPanning(true);
      setStartPan({
        x: e.clientX - panPosition.x,
        y: e.clientY - panPosition.y
      });
      
      // Add a class to indicate panning is active
      if (containerRef.current) {
        containerRef.current.classList.add('is-panning');
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Handle panning when mouse is down
    if (isPanning) {
      const newPanPosition = {
        x: e.clientX - startPan.x,
        y: e.clientY - startPan.y
      };
      setPanPosition(newPanPosition);
    }
    
    // Handle temp connection drawing when in connecting mode
    if (isConnecting && tempConnection && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      
      // Calculate mouse position in canvas coordinates
      const mouseX = (e.clientX - rect.left);
      const mouseY = (e.clientY - rect.top);
      
      setTempConnection(prev => ({
        ...prev!,
        end: {
          x: mouseX,
          y: mouseY
        }
      }));
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    // Reset panning state
    if (containerRef.current) {
      containerRef.current.classList.remove('is-panning');
    }
  };

  const handleConnectionDelete = (connectionId: string) => {
    if (!workflow) return;
    const updatedWorkflow: Workflow = {
      ...workflow,
      connections: workflow.connections.filter(conn => conn.id !== connectionId),
    };
    updateWorkflow(updatedWorkflow);
  };

  // Dedicated zoom functions with improved functionality
  const zoomIn = () => {
    // Use throttling to prevent too rapid zooming
    if (lastZoomTimeRef.current && Date.now() - lastZoomTimeRef.current < 100) {
      return;
    }
    lastZoomTimeRef.current = Date.now();
    
    setScale(prevScale => {
      const newScale = Math.min(prevScale + 0.1, 2.0);
      return parseFloat(newScale.toFixed(1)); // Round to one decimal place for consistency
    });
  };

  const zoomOut = () => {
    // Use throttling to prevent too rapid zooming
    if (lastZoomTimeRef.current && Date.now() - lastZoomTimeRef.current < 100) {
      return;
    }
    lastZoomTimeRef.current = Date.now();
    
    setScale(prevScale => {
      const newScale = Math.max(prevScale - 0.1, 0.3);
      return parseFloat(newScale.toFixed(1)); // Round to one decimal place for consistency
    });
  };

  const resetZoom = () => {
    setScale(1);
    setPanPosition({ x: 0, y: 0 });
  };
  
  // Handle wheel zoom with mouse scroll
  const handleZoom = (e: React.WheelEvent<HTMLDivElement>) => {
    // Always prevent default to stop page scrolling
    e.preventDefault();
    e.stopPropagation();
    
    // Use throttling to prevent too many updates (30ms = ~33fps is good balance)
    if (lastZoomTimeRef.current && Date.now() - lastZoomTimeRef.current < 30) {
      return;
    }
    lastZoomTimeRef.current = Date.now();
    
    // Get mouse position relative to the canvas
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Determine zoom direction (negative deltaY = scroll up = zoom in)
    const zoomDelta = -e.deltaY * 0.001; // Sensitivity adjustment
    
    // Use current values from refs for smooth calculation
    const currentScale = scaleRef.current;
    const currentPan = panPositionRef.current;
    
    // Calculate new scale
    const newScale = Math.min(Math.max(currentScale + zoomDelta, 0.3), 2.0);
    
    // Calculate the point in canvas coordinates before zoom
    const canvasX = (mouseX - currentPan.x) / currentScale;
    const canvasY = (mouseY - currentPan.y) / currentScale;
    
    // Calculate new pan position to keep the same canvas point under the mouse
    const newPanX = mouseX - (canvasX * newScale);
    const newPanY = mouseY - (canvasY * newScale);
    
    // Update states - React 18 will batch these automatically
    setScale(newScale);
    setPanPosition({ x: newPanX, y: newPanY });
    // Force immediate connection redraw to prevent lag
    setForceUpdate(prev => prev + 1);
  };

  // Handle press and hold on connection button
  const handleConnectionButtonDown = (e: React.MouseEvent, workflowBlock: WorkflowBlock) => {
    e.stopPropagation();
    e.preventDefault();
    
    setIsConnecting(true);
    setSourceBlock(workflowBlock);
    
    // Get the canvas container
    const canvasContainer = document.querySelector('.canvas-container');
    if (!canvasContainer) return;
    
    const canvasRect = canvasContainer.getBoundingClientRect();
    
    // Calculate the connection start position using the workflowBlock's position
    const blockPos = workflowBlock.position;
    const startX = blockPos.x + 256; // Right side of block
    const startY = blockPos.y + 32; // Middle of block height
    
    // Apply current scale and pan offset
    const scaledStartX = (startX * scale) + panPosition.x;
    const scaledStartY = (startY * scale) + panPosition.y;
    
    setTempConnection({
      start: {
        x: scaledStartX,
        y: scaledStartY,
      },
      end: {
        x: e.clientX - canvasRect.left,
        y: e.clientY - canvasRect.top,
      },
    });
  };

  // Add a new function to handle block deletion
  const handleBlockDelete = (blockId: string) => {
    if (!workflow) return;

    // Find and delete all connections connected to this block
    const connectionsToRemove = workflow.connections.filter(
      conn => conn.sourceId === blockId || conn.targetId === blockId
    );
    
    const updatedConnections = workflow.connections.filter(
      conn => conn.sourceId !== blockId && conn.targetId !== blockId
    );
    
    const updatedWorkflow: Workflow = {
      ...workflow,
      blocks: workflow.blocks.filter(block => block.id !== blockId),
      connections: updatedConnections
    };
    
    updateWorkflow(updatedWorkflow);
  };

  // Check if connection already exists and prevent creating multiple connections from one output
  const handleConnectionButtonClick = (e: React.MouseEvent, block: WorkflowBlock, routeId?: string, routeIndex?: number, totalRoutes?: number) => {
    e.stopPropagation();
    
    if (isConnecting && sourceBlock?.id === block.id) {
      // Cancel connection if clicking the source block again
      setIsConnecting(false);
      setSourceBlock(null);
      setTempConnection(null);
      setSelectedRouteId(null);
    } else if (isConnecting && sourceBlock) {
      // Get source and target block types
      const sourceBlockData = blocks.find(b => b.id === sourceBlock.blockId);
      const targetBlockData = blocks.find(b => b.id === block.blockId);
      const isSourceStartBlock = sourceBlockData?.id === 'start-block';
      const isTargetResultsBlock = targetBlockData?.id === 'results-block';
      const isSourceDecisionRouter = sourceBlockData?.id === 'decision-router';
      
      // Check if connection already exists between these specific blocks
      // For decision routers, check if this specific route already has a connection
      const connectionExists = workflow?.connections.some(
        conn => conn.sourceId === sourceBlock.id && conn.targetId === block.id && 
        (!selectedRouteId || (conn as any).routeId === selectedRouteId)
      );
      
      if (connectionExists) {
        alert("A connection already exists between these blocks for this route.");
        return;
      }
      
      // For decision routers, allow multiple outgoing connections (one per route)
      // For non-Start blocks and non-decision-router blocks, check if they already have an incoming connection
      if (!isSourceStartBlock && !isSourceDecisionRouter) {
        // Check if the source already has an outgoing connection (except for Start block and Decision Router)
        const hasOutgoingConnection = workflow?.connections.some(
          conn => conn.sourceId === sourceBlock.id
        );
        
        if (hasOutgoingConnection) {
          alert("This block already has an outgoing connection. Remove it first before creating a new one.");
          return;
        }
      }
      
      // Regular blocks (not Results) can have only one incoming connection
      if (!isTargetResultsBlock) {
        // Check if target already has an incoming connection
        const hasIncomingConnection = workflow?.connections.some(
          conn => conn.targetId === block.id
        );
        
        if (hasIncomingConnection) {
          alert("This block already has an incoming connection. Remove it first before creating a new one.");
          return;
        }
      }
      
      // Attempt to create connection between source and target
      if (canBeConnectionTarget(blocks.find(b => b.id === block.blockId)?.id || '')) {
        // CRITICAL: Decision Router connections MUST have a routeId
        if (isSourceDecisionRouter && !selectedRouteId) {
          console.error('❌ ERROR: Attempted to create connection from Decision Router without routeId!', {
            sourceBlock: sourceBlock.id,
            targetBlock: block.id,
            sourceBlockData: sourceBlockData?.id,
            selectedRouteId
          });
          alert('Error: Connections from Decision Router must specify a route. Please click on a route handle to create a connection.');
          setIsConnecting(false);
          setSourceBlock(null);
          setTempConnection(null);
          setSelectedRouteId(null);
          return;
        }
        
        const newConnection: Connection & { routeId?: string } = {
          id: `conn-${Date.now()}`,
          sourceId: sourceBlock.id,
          targetId: block.id,
        };
        
        // Add routeId if this is a decision router connection
        if (isSourceDecisionRouter && selectedRouteId) {
          newConnection.routeId = selectedRouteId;
          console.log('🔗 Creating connection with routeId:', selectedRouteId, 'for connection:', newConnection.id);
        } else if (isSourceDecisionRouter) {
          // This should never happen due to the check above, but log it just in case
          console.error('❌ CRITICAL: Decision Router connection created without routeId!', {
            connectionId: newConnection.id,
            sourceBlock: sourceBlock.id,
            selectedRouteId
          });
        } else {
          console.log('⚠️ Creating connection WITHOUT routeId for connection:', newConnection.id, '(non-Decision Router connection)');
        }
        
        if (workflow) {
          const updatedWorkflow = {
            ...workflow,
            connections: [...workflow.connections, newConnection as Connection],
          };
          updateWorkflow(updatedWorkflow);
        }
        
        setIsConnecting(false);
        setSourceBlock(null);
        setTempConnection(null);
        setSelectedRouteId(null);
      }
    } else {
      // Start new connection from this block (or from a specific route)
      if (canBeConnectionSource(blocks.find(b => b.id === block.blockId)?.id || '')) {
        setIsConnecting(true);
        setSourceBlock(block);
        setSelectedRouteId(routeId || null);
        
        // Initialize temp connection with start position
        // End position will be updated in handleMouseMove
        if (block.position) {
          let startY = block.position.y + 32; // Default: center of block height (64px / 2)
          
          // If this is a route connection, calculate the vertical position of the route
          if (routeId && routeIndex !== undefined && totalRoutes !== undefined) {
            const spacing = 100 / (totalRoutes + 1);
            const topPosition = spacing * (routeIndex + 1);
            
            // Calculate block height - use actual DOM height or estimate for Decision Router
            let blockHeight = 64;
            const sourceElement = document.querySelector(`[style*="left: ${block.position.x}px"][style*="top: ${block.position.y}px"]`);
            if (sourceElement) {
              blockHeight = sourceElement.getBoundingClientRect().height / scale;
            } else {
              // Estimate height for Decision Router when DOM not ready
              blockHeight = Math.max(120, 80 + (totalRoutes * 35));
            }
            
            startY = block.position.y + (topPosition / 100) * blockHeight;
          }
          
          setTempConnection({
            start: {
              x: block.position.x + 256, // Right edge of block (256px width)
              y: startY,
            },
            end: {
              x: block.position.x + 256, // Initial end is same as start
              y: startY,
            }
          });
        }
      }
    }
  };

  // Add an effect to handle mouse movement for connections and dragging
  React.useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      // Update mouse position for temporary connections
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left + containerRef.current.scrollLeft;
        const y = e.clientY - rect.top + containerRef.current.scrollTop;
        setMousePosition({ x, y });
      }
      
      // Handle temp connection drawing
      if (isConnecting && sourceBlock && sourceBlock.position && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = (e.clientX - rect.left + containerRef.current.scrollLeft - panPosition.x) / scale;
        const mouseY = (e.clientY - rect.top + containerRef.current.scrollTop - panPosition.y) / scale;
        
        // Update temporary connection with dynamic calculation
        const mousePos = { x: mouseX, y: mouseY };
        
        // Get the connection start position from the source block
        // We will use the source block connection point directly to avoid visual jumps
        if (sourceBlock && sourceBlock.position) {
          const startPos = sourceBlock.position;
          
          // Update the temp connection state with updated start and end points
          setTempConnection({
            start: { 
              x: sourceBlock.position.x + 128, // Center of the block width
              y: sourceBlock.position.y + 32   // Center of the block height
            },
            end: { x: mouseX, y: mouseY }
          });
        }
      }
      
      // Handle block dragging
      if (isDragging && draggedBlockId && containerRef.current && workflow) {
        // Cancel any existing animation frame to prevent queuing multiple updates
        if (requestAnimationFrameRef.current) {
          cancelAnimationFrame(requestAnimationFrameRef.current);
        }
        
        // Use requestAnimationFrame for smooth updates
        requestAnimationFrameRef.current = requestAnimationFrame(() => {
          const rect = containerRef.current!.getBoundingClientRect();
          const mouseX = (e.clientX - rect.left + containerRef.current!.scrollLeft - panPosition.x) / scale;
          const mouseY = (e.clientY - rect.top + containerRef.current!.scrollTop - panPosition.y) / scale;
          
          // Calculate new position with offset
          const newX = mouseX + dragOffsetRef.current.x;
          const newY = mouseY + dragOffsetRef.current.y;
          
          // First, directly update the DOM for immediate visual feedback
          if (draggedElementRef.current) {
            draggedElementRef.current.style.left = `${newX}px`;
            draggedElementRef.current.style.top = `${newY}px`;
          }
          
          // Find the current block that's being dragged
          const currentBlock = workflow.blocks.find(block => block.id === draggedBlockId);
          
          if (currentBlock) {
            // Then update the state for React to reconcile (which also updates connections)
            const updatedWorkflow = {...workflow};
            
            // Update the block position in our copy
            updatedWorkflow.blocks = workflow.blocks.map(block => 
              block.id === draggedBlockId 
                ? { ...block, position: { x: newX, y: newY } } 
                : block
            );
            
            // Use this batch update approach to ensure everything renders in one go
            setWorkflow(updatedWorkflow);
          }
        });
      }
    };
    
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        // Cancel any pending animation frame
        if (requestAnimationFrameRef.current) {
          cancelAnimationFrame(requestAnimationFrameRef.current);
          requestAnimationFrameRef.current = null;
        }
        
        // Clean up dragging optimizations
        if (draggedElementRef.current) {
          draggedElementRef.current.style.willChange = '';
          draggedElementRef.current = null;
        }
        
        // Remove dragging class when done
        if (containerRef.current) {
          containerRef.current.classList.remove('block-dragging');
        }
        
        setIsDragging(false);
        setDraggedBlockId(null);
        
        // Save the workflow after moving a block
        if (workflow) {
          saveWorkflow(workflow);
        }
      }
    };
    
    // Add event listeners
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isConnecting, sourceBlock, isDragging, draggedBlockId, workflow, scale, panPosition]);

  // Refresh connections when canvas is panned or zoomed to keep them aligned
  React.useEffect(() => {
    // Trigger a re-render to update connection positions when panning or zooming
    setForceUpdate(prev => prev + 1);
  }, [panPosition, scale]);

  // Update the executeWorkflow function to handle detailed outputs
  const executeWorkflow = React.useCallback(async () => {
    if (isExecuting || !workflow) return;

    try {
      setIsExecuting(true);
      setExecutionResults(null); // Clear previous results
      setBlockData({}); // Reset block data
      
      // We no longer need to explicitly get the OpenAI API key from localStorage
      // The backend will use its stored API key
      console.log('Executing workflow with backend API key');
      
      // Just pass the start text to the workflow - backend will handle the API key
      const result = await api.executeWorkflow(workflow.id, { 
        startText
      });
      
      setExecutionId(result.executionId);
      setExecutionStatus('running');
      
      // Poll for status updates
      const pollInterval = setInterval(async () => {
        try {
          const execution = await api.getWorkflowExecution(result.executionId);
          setExecutionStatus(execution.status);
          
          if (execution.status === 'completed' || execution.status === 'failed') {
            clearInterval(pollInterval);
            setIsExecuting(false);
            
            // Update results text if execution completed successfully
            if (execution.status === 'completed' && execution.outputs) {
              setExecutionResults(execution.outputs);
              
              // Parse the new nested results format
              if (execution.outputs.results && Array.isArray(execution.outputs.results)) {
                console.log("Received nested results:", execution.outputs.results);
                
                // Extract block outputs from the new nested format
                const blockOutputs: Record<string, string> = {};
                const blockToNameMapping: Record<string, string> = {};
                const blockActualModels: Record<string, string> = {};
                
                // Keep track of which blocks we've already mapped to handle duplicates
                const usedBlocks = new Set<string>();
                
                // Process each result item
                execution.outputs.results.forEach((item: any, index: number) => {
                  console.log(`Processing result item ${index}:`, item);
                  
                                      if (item.type === 'sequential' && item.chain) {
                    console.log('Found sequential chain:', item.chain);
                    // Handle sequential chain - map each step to its workflow block ID
                    const chainBlocks = getSequentialChainBlocks(workflow);
                    console.log('Sequential chain blocks from detection:', chainBlocks);
                    console.log('Sequential chain steps from API:', item.chain);
                    
                    item.chain.forEach((step: any, stepIndex: number) => {
                      console.log(`Processing sequential step ${stepIndex}:`, step);
                      
                      // Use the ID directly from the backend response
                      if (step.id) {
                        const blockId = step.id;
                        blockOutputs[blockId] = typeof step.output === 'object' 
                          ? JSON.stringify(step.output, null, 2) 
                          : String(step.output);
                        blockToNameMapping[blockId] = step.name;
                        if (step.actualModel) {
                          blockActualModels[blockId] = step.actualModel;
                        }
                        usedBlocks.add(blockId);
                        console.log(`✅ Mapped sequential step "${step.name}" to block ${blockId} using ID`);
                      } else {
                        console.error(`❌ No ID in sequential step:`, step);
                      }
                    });
                  } else if (item.id && item.output !== undefined) {
                    console.log(`Processing parallel block "${item.name}" with ID "${item.id}"`);
                    
                    // Use the ID directly from the backend response
                      let outputText = '';
                      if (typeof item.output === 'object') {
                        if (Array.isArray(item.output)) {
                          outputText = JSON.stringify(item.output, null, 2);
                        } else if (item.output && typeof item.output === 'object') {
                          outputText = JSON.stringify(item.output, null, 2);
                        } else {
                          outputText = String(item.output);
                        }
                      } else {
                        outputText = String(item.output);
                      }
                      
                    blockOutputs[item.id] = outputText;
                    blockToNameMapping[item.id] = item.name;
                    if (item.actualModel) {
                      blockActualModels[item.id] = item.actualModel;
                    }
                    usedBlocks.add(item.id);
                    console.log(`✅ Mapped parallel block "${item.name}" to block ${item.id} using ID`);
                    } else {
                    console.warn('Skipping result item with missing ID or output:', item);
                  }
                });
                
                console.log('Final block outputs mapping:', blockOutputs);
                console.log('Final block to name mapping:', blockToNameMapping);
                console.log('Workflow blocks for reference:', workflow.blocks.map(wb => {
                  const block = blocks.find(b => b.id === wb.blockId);
                  return {
                    workflowBlockId: wb.id,
                    blockName: block?.name,
                    blockType: block?.category,
                    hasOutput: !!blockOutputs[wb.id]
                  };
                }));
                
                // Create a map of block inputs and outputs
                const newBlockData: Record<string, { input: string, output: string, actualModel?: string }> = {};
                
                // For start blocks, the input is the startText and output is the startText (passthrough)
                workflow.blocks.forEach(workflowBlock => {
                  const blockType = blocks.find(b => b.id === workflowBlock.blockId)?.id;
                  
                  if (blockType === 'start-block') {
                    newBlockData[workflowBlock.id] = {
                      input: startText,
                      output: startText // Start block just passes through the input
                    };
                  } else {
                    // For non-start blocks, find the incoming connection to determine the input
                    const incomingConnections = workflow.connections.filter(c => c.targetId === workflowBlock.id);
                    let inputText = '';
                    
                    if (incomingConnections.length > 0) {
                      if (incomingConnections.length === 1) {
                        // Single input connection
                        const sourceBlockType = workflow.blocks.find(b => b.id === incomingConnections[0].sourceId);
                        const sourceBlockTypeId = blocks.find(b => b.id === sourceBlockType?.blockId)?.id;
                        
                        if (sourceBlockTypeId === 'start-block') {
                          inputText = startText;
                        } else {
                          inputText = blockOutputs[incomingConnections[0].sourceId] || '';
                        }
                      } else {
                        // Multiple input connections - combine them
                        const inputs: string[] = [];
                        incomingConnections.forEach(conn => {
                          const sourceBlockType = workflow.blocks.find(b => b.id === conn.sourceId);
                          const sourceBlockTypeId = blocks.find(b => b.id === sourceBlockType?.blockId)?.id;
                          
                          if (sourceBlockTypeId === 'start-block') {
                            inputs.push(startText);
                          } else {
                            inputs.push(blockOutputs[conn.sourceId] || '');
                          }
                        });
                        inputText = inputs.join('\n\n---\n\n');
                      }
                    }
                    
                    // Set the block data regardless of whether we have output - this helps with partial results
                    const blockType = blocks.find(b => b.id === workflowBlock.blockId);
                    newBlockData[workflowBlock.id] = {
                      input: inputText,
                      output: blockOutputs[workflowBlock.id] || '',
                      actualModel: blockActualModels[workflowBlock.id]
                    };
                    
                    console.log(`Set block data for ${blockType?.name || 'unknown'} (${workflowBlock.id}):`, {
                      input: inputText.substring(0, 100) + (inputText.length > 100 ? '...' : ''),
                      output: (blockOutputs[workflowBlock.id] || '').substring(0, 100) + 
                             ((blockOutputs[workflowBlock.id] || '').length > 100 ? '...' : ''),
                      hasOutput: !!blockOutputs[workflowBlock.id],
                      actualModel: blockActualModels[workflowBlock.id]
                    });
                  }
                });
                
                // Create combined results text from all outputs BEFORE setting block data
                const combinedResult: Record<string, any> = {};
                execution.outputs.results.forEach((item: any) => {
                  if (item.type === 'sequential') {
                    combinedResult.sequentialChain = item.chain;
                  } else {
                    const key = item.type || item.name?.toLowerCase().replace(/\s+/g, '_') || 'output';
                    combinedResult[key] = item.output;
                  }
                });
                
                const combinedResultsText = JSON.stringify(combinedResult, null, 2);
                setResultsText(combinedResultsText);
                
                // Map the combined results to the Results block if it exists
                const resultsBlock = workflow.blocks.find(workflowBlock => {
                  const block = blocks.find(b => b.id === workflowBlock.blockId);
                  return block && block.name.toLowerCase().includes('results');
                });
                
                if (resultsBlock) {
                  blockOutputs[resultsBlock.id] = combinedResultsText;
                  blockToNameMapping[resultsBlock.id] = 'Combined Results';
                  
                  // Add Results block to newBlockData
                  const resultsInputConnections = workflow.connections.filter(c => c.targetId === resultsBlock.id);
                  let resultsInputText = '';
                  
                  console.log('🎯 Results block connections:', resultsInputConnections.length);
                  console.log('🎯 Available blockOutputs keys:', Object.keys(blockOutputs));
                  
                  if (resultsInputConnections.length > 0) {
                    const inputs: string[] = [];
                    resultsInputConnections.forEach(conn => {
                      console.log(`  📌 Processing connection from ${conn.sourceId} to Results`);
                      const sourceBlockType = workflow.blocks.find(b => b.id === conn.sourceId);
                      const sourceBlockTypeId = blocks.find(b => b.id === sourceBlockType?.blockId)?.id;
                      
                      console.log(`     Source block type: ${sourceBlockTypeId}`);
                      console.log(`     Has output in blockOutputs: ${!!blockOutputs[conn.sourceId]}`);
                      
                      if (sourceBlockTypeId === 'start-block') {
                        inputs.push(startText);
                      } else {
                        const output = blockOutputs[conn.sourceId] || '';
                        if (output) {
                          console.log(`     ✅ Adding output from ${conn.sourceId} (${output.substring(0, 100)}...)`);
                        } else {
                          console.log(`     ⚠️  No output found for ${conn.sourceId}`);
                        }
                        inputs.push(output);
                      }
                    });
                    resultsInputText = inputs.join('\n\n---\n\n');
                  }
                  
                  console.log(`🎯 Final resultsInputText length: ${resultsInputText.length}`);
                  
                  // Get actualModel from the connected block(s)
                  let resultsActualModel: string | undefined;
                  if (resultsInputConnections.length > 0) {
                    // Use the actualModel from the first connected block that has one
                    for (const conn of resultsInputConnections) {
                      if (blockActualModels[conn.sourceId]) {
                        resultsActualModel = blockActualModels[conn.sourceId];
                        break;
                      }
                    }
                  }
                  
                  newBlockData[resultsBlock.id] = {
                    input: resultsInputText,
                    output: combinedResultsText,
                    actualModel: resultsActualModel
                  };
                }
                
                console.log('Final newBlockData with Results block:', newBlockData);
                setBlockData(newBlockData);
              } else {
                console.log("No 'results' field found in execution outputs");
                console.log("Available outputs:", Object.keys(execution.outputs));
                setResultsText('No results available');
              }
              
              // Log the successful execution
              console.log('Workflow executed successfully:', execution.outputs);
            } else if (execution.status === 'failed') {
              // Handle failed execution
              setResultsText('Execution failed: ' + (execution.error || 'Unknown error'));
              console.error('Workflow execution failed:', execution.error);
            }
          }
        } catch (err) {
          console.error('Failed to poll execution status:', err);
          clearInterval(pollInterval);
          setIsExecuting(false);
          setExecutionStatus('failed');
        }
      }, 2000);
      
    } catch (err) {
      console.error('Failed to execute workflow:', err);
      setIsExecuting(false);
      alert(`Failed to execute workflow: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [workflow, isExecuting, startText, blocks]);

  // Helper function to find all sequential chains in the workflow
  const getSequentialChainBlocks = (workflow: Workflow) => {
    console.log('Finding sequential chain blocks...');
    
    // Find the start block
    const startBlock = workflow.blocks.find(block => {
      const blockType = blocks.find(b => b.id === block.blockId)?.id;
      return blockType === 'start-block';
    });
    
    if (!startBlock) {
      console.log('No start block found');
      return [];
    }
    
    console.log('Start block:', startBlock);
    
    // Simple path finder: follow all paths from start and collect them
    const findAllPaths = (currentId: string, currentPath: string[] = []): string[][] => {
      const paths: string[][] = [];
      
      // Get outgoing connections from current block
      const outgoing = workflow.connections.filter(c => c.sourceId === currentId);
      console.log(`Block ${currentId} has ${outgoing.length} outgoing connections`);
      
      if (outgoing.length === 0) {
        // End of path
        return [currentPath];
      }
      
      outgoing.forEach(conn => {
        const targetBlock = workflow.blocks.find(b => b.id === conn.targetId);
        if (!targetBlock) return;
        
        const targetBlockType = blocks.find(b => b.id === targetBlock.blockId)?.id;
        console.log(`Following path to ${conn.targetId} (${targetBlockType})`);
        
        if (targetBlockType === 'results-block') {
          // End of this path at Results block
          paths.push(currentPath);
        } else {
          // Continue the path
          const newPath = [...currentPath, conn.targetId];
          const subPaths = findAllPaths(conn.targetId, newPath);
          paths.push(...subPaths);
        }
      });
      
      return paths;
    };
    
    // Find all paths from start
    const allPaths = findAllPaths(startBlock.id);
    console.log('All paths from start:', allPaths);
    
    // Filter to get sequential chains (paths with 2+ blocks)
    const sequentialChains = allPaths.filter(path => path.length >= 2);
    console.log('Sequential chains (2+ blocks):', sequentialChains);
    
    // Return the longest sequential chain
    const longestChain = sequentialChains.reduce((longest, current) => {
      return current.length > longest.length ? current : longest;
    }, []);
    
    console.log('Selected longest sequential chain:', longestChain);
    console.log('Chain block details:', longestChain.map(blockId => {
      const workflowBlock = workflow.blocks.find(wb => wb.id === blockId);
      const block = blocks.find(b => b.id === workflowBlock?.blockId);
      return { blockId, blockName: block?.name };
    }));
    
    return longestChain;
  };

  // Helper function to find workflow block by name or type, handling duplicates
  const findWorkflowBlockByNameOrType = (
    workflow: Workflow, 
    itemName: string, 
    itemType: string, 
    blocks: Block[], 
    usedBlocks: Set<string>
  ) => {
    console.log(`Finding workflow block for "${itemName}" of type "${itemType}"`);
    console.log('Available workflow blocks:', workflow.blocks.map(wb => {
      const block = blocks.find(b => b.id === wb.blockId);
      return { id: wb.id, blockId: wb.blockId, name: block?.name, used: usedBlocks.has(wb.id) };
    }));
    
    // First, try to find by exact name match (most accurate)
    let candidate = workflow.blocks.find(workflowBlock => {
      if (usedBlocks.has(workflowBlock.id)) return false;
      const block = blocks.find(b => b.id === workflowBlock.blockId);
      return block && block.name === itemName;
    });
    
    if (candidate) {
      console.log(`Found exact name match for "${itemName}":`, candidate);
      return candidate;
    }
    
    // Try fuzzy name matching (case-insensitive, partial matches)
    candidate = workflow.blocks.find(workflowBlock => {
      if (usedBlocks.has(workflowBlock.id)) return false;
      const block = blocks.find(b => b.id === workflowBlock.blockId);
      if (!block) return false;
      
      const blockNameLower = block.name.toLowerCase();
      const itemNameLower = itemName.toLowerCase();
      
      // Check for partial matches
      return blockNameLower.includes(itemNameLower) || itemNameLower.includes(blockNameLower);
    });
    
    if (candidate) {
      console.log(`Found fuzzy name match for "${itemName}":`, candidate);
      return candidate;
    }
    
    // If no name match, try to find by type matching
    candidate = workflow.blocks.find(workflowBlock => {
      if (usedBlocks.has(workflowBlock.id)) return false;
      const block = blocks.find(b => b.id === workflowBlock.blockId);
      if (!block) return false;
      
      // Map API result types to block names
      let matches = false;
      const blockNameLower = block.name.toLowerCase();
      
      switch (itemType) {
        case 'sentimentAnalysis':
          matches = blockNameLower.includes('sentiment');
          break;
        case 'keywordExtraction':
          matches = blockNameLower.includes('keyword') || blockNameLower.includes('extract');
          break;
        case 'textSummarization':
          matches = blockNameLower.includes('summariz') || 
                   blockNameLower.includes('summar');
          break;
        case 'namedEntityRecognition':
          matches = blockNameLower.includes('entity') || 
                   blockNameLower.includes('ner') ||
                   blockNameLower.includes('recognition');
          break;
        case 'textClassification':
          matches = blockNameLower.includes('classif');
          break;
        case 'topicModeling':
          matches = blockNameLower.includes('topic');
          break;
        case 'textSimilarity':
          matches = blockNameLower.includes('similarity');
          break;
        default:
          // For unknown types, try to match by name similarity
          matches = blockNameLower.includes(itemName.toLowerCase()) ||
                   itemName.toLowerCase().includes(blockNameLower);
      }
      
      if (matches) {
        console.log(`Found type match for "${itemName}" (${itemType}): ${block.name}`);
      }
      return matches;
    });
    
    if (candidate) {
      console.log(`Found type match for "${itemName}":`, candidate);
    } else {
      console.log(`No match found for "${itemName}" of type "${itemType}"`);
    }
    
    return candidate;
  };

  // Function to get block color based on type
  const getBlockColor = (blockId: string): string => {
    if (blockId === 'start-block') return 'bg-blue-50 border-blue-500 border-2';
    if (blockId === 'results-block') return 'bg-green-50 border-green-500 border-2';
    if (blockId === 'decision-router') return 'bg-orange-50 border-orange-500 border-2';
    return 'bg-white';
  };

  // Get sidebar block styling (for catalog display)
  const getSidebarBlockStyle = (blockId: string): string => {
    if (blockId === 'start-block') return 'bg-blue-50 border-2 border-blue-500';
    if (blockId === 'results-block') return 'bg-green-50 border-2 border-green-500';
    if (blockId === 'decision-router') return 'bg-orange-50 border-2 border-orange-500';
    return 'bg-white';
  };
  
  // Get category badge color
  const getCategoryBadgeStyle = (blockId: string): string => {
    if (blockId === 'start-block') return 'bg-blue-100 text-blue-800';
    if (blockId === 'results-block') return 'bg-green-100 text-green-800';
    if (blockId === 'decision-router') return 'bg-orange-100 text-orange-800';
    return 'bg-blue-100 text-blue-800';
  };

  // Function to render block content with test button
  const renderBlockContent = (block: Block, workflowBlock: WorkflowBlock) => {
    const blockResult = blockData[workflowBlock.id];
    const hasExecutionData = blockResult && executionStatus === 'completed';
    
    if (block.id === 'start-block') {
      return (
        <>
          <p className="text-sm text-gray-500 mb-2">{block.description}</p>
          <textarea
            className="w-full p-2 border border-gray-300 rounded-md text-sm"
            placeholder="Enter input text..."
            rows={2}
            value={startText}
            onChange={(e) => setStartText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="mt-2 flex justify-end">
            <button
              className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md text-sm font-medium flex items-center"
              onClick={(e) => {
                e.stopPropagation();
                executeWorkflow();
              }}
              disabled={isExecuting}
            >
              {isExecuting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Running...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Run Workflow
                </>
              )}
            </button>
          </div>
          
          {hasExecutionData && (
            <div className="mt-3">
              <div className="text-xs font-medium text-gray-500">Output:</div>
              <div className="mt-1 text-xs bg-gray-50 p-2 rounded border border-gray-200 max-h-20 overflow-y-auto whitespace-pre-wrap">
                {blockResult.output}
              </div>
            </div>
          )}
        </>
      );
    } else if (block.id === 'decision-router') {
      // Special rendering for decision router blocks
      const decisionData = (workflowBlock as any).data || {};
      const configuredModel = decisionData.model || 'Not configured';
      const configuredRoutes = decisionData.routes || [];
      
      // DEBUG: Log what we have
      console.log('🔍 Decision Router Debug:', {
        workflowBlockId: workflowBlock.id,
        hasExecutionData,
        executionStatus,
        blockResult,
        blockData: Object.keys(blockData),
        output: blockResult?.output
      });
      
      // Check if we have execution results to show chosen route
      let chosenRouteInfo: { name: string; color: string; modelUsed?: string; inputText?: string } | null = null;
      if (hasExecutionData && blockResult.output) {
        try {
          const decisionOutput = typeof blockResult.output === 'string' 
            ? JSON.parse(blockResult.output) 
            : blockResult.output;
          
          console.log('🔍 Decision Output Parsed:', decisionOutput);
          
          if (decisionOutput && decisionOutput.routeName) {
            const chosenRoute = configuredRoutes.find((r: any) => 
              r.id === decisionOutput.chosenRoute || r.name === decisionOutput.routeName
            );
            chosenRouteInfo = {
              name: decisionOutput.routeName,
              color: chosenRoute?.color || '#4CAF50',
              modelUsed: decisionOutput.modelUsed,
              inputText: decisionOutput.inputText
            };
            console.log('✅ Chosen Route Info:', chosenRouteInfo);
          } else {
            console.log('❌ No routeName in decision output');
          }
        } catch (e) {
          console.error('Failed to parse decision output:', e);
        }
      } else {
        console.log('❌ No execution data or block result');
      }
      
      return (
        <>
          <p className="text-sm text-gray-500 mb-2">{block.description}</p>
          
          {/* ALWAYS SHOW: Decision Model */}
          <div className="mt-2">
            <div className="text-xs font-medium text-gray-500 mb-1">Decision Model:</div>
            <div className="text-xs bg-blue-50 p-2 rounded border border-blue-200 text-blue-700 font-mono">
              {chosenRouteInfo?.modelUsed || configuredModel}
                        </div>
              </div>
              
          {/* ALWAYS SHOW: Routes - with CHOSEN ROUTE highlighted after execution */}
          <div className="mt-2">
            <div className="text-xs font-medium text-gray-500 mb-1">
              Routes ({configuredRoutes.length}):
              {chosenRouteInfo && (
                <span className="ml-2 text-green-600 font-bold">✓ Route Selected</span>
              )}
                </div>
            <div className="flex flex-wrap gap-1">
              {configuredRoutes.map((route: any) => {
                const isChosen = chosenRouteInfo && route.name === chosenRouteInfo.name;
                return (
                  <span 
                    key={route.id}
                    className={`text-xs px-2 py-1 rounded border font-medium transition-all ${
                      isChosen 
                        ? 'ring-2 ring-offset-1 shadow-lg scale-110 font-bold' 
                        : ''
                    }`}
                  style={{ 
                      backgroundColor: isChosen ? route.color : (route.color + '20'),
                      borderColor: route.color,
                      color: isChosen ? 'white' : route.color
                    }}
                  >
                    {isChosen && '✓ '}{route.name}
                  </span>
                );
              })}
                </div>
          </div>
          
          {/* Show input text after execution */}
          {chosenRouteInfo?.inputText && (
            <div className="mt-2">
              <div className="text-xs font-medium text-gray-500 mb-1">Input Evaluated:</div>
              <div className="text-xs bg-gray-50 p-2 rounded border border-gray-200 max-h-12 overflow-y-auto text-gray-700 whitespace-pre-wrap font-medium">
                {chosenRouteInfo.inputText}
                    </div>
              </div>
          )}
          
          {/* Configuration hint */}
          <div className="mt-3 pt-2 border-t border-orange-200">
            <div className="flex items-center justify-center text-xs text-orange-600">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              <span className="font-medium">Double-click to configure</span>
            </div>
          </div>
        </>
      );
    } else if (block.id === 'results-block') {
      return (
        <>
          <p className="text-sm text-gray-500 mb-2">{block.description}</p>
          
          {hasExecutionData && blockResult.output && (
            <div className="mt-2">
              <div className="text-xs font-medium text-gray-500 mb-1">Output:</div>
              <div className="mt-1 text-xs bg-gray-50 p-2 rounded border border-gray-200 max-h-40 overflow-y-auto whitespace-pre-wrap">
                {blockResult.output}
              </div>
              {blockResult.actualModel && (
                <div className="mt-1 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded px-2 py-1 inline-block">
                  <span className="font-semibold">Model:</span> {blockResult.actualModel}
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-between items-center mt-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {block.category}
            </span>
          </div>
        </>
      );
    } else {
      // Standard block rendering
      return (
        <>
          <p className="text-sm text-gray-500 mb-2">{block.description}</p>
          
          {hasExecutionData && (
            <div className="mt-3">
                <div className="text-xs font-medium text-gray-500">Input:</div>
                <div className="mt-1 text-xs bg-gray-50 p-2 rounded border border-gray-200 max-h-20 overflow-y-auto whitespace-pre-wrap">
                  {blockResult.input}
                </div>
              </div>
          )}
              
          {hasExecutionData && (
            <div className="mt-3">
                <div className="text-xs font-medium text-gray-500">Output:</div>
                <div className="mt-1 text-xs bg-gray-50 p-2 rounded border border-gray-200 max-h-20 overflow-y-auto whitespace-pre-wrap">
                  {blockResult.output}
                </div>
                {blockResult.actualModel && (
                  <div className="mt-1 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded px-2 py-1 inline-block">
                    <span className="font-semibold">Model:</span> {blockResult.actualModel}
                  </div>
                )}
              </div>
          )}
          
          <div className="flex justify-between items-center mt-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {block.category}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleTestBlock(workflowBlock);
              }}
              className="px-2 py-1 rounded text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 flex items-center"
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Test
            </button>
          </div>
        </>
      );
    }
  };

  const handleTestBlock = (workflowBlock: WorkflowBlock) => {
    const block = blocks.find(b => b.id === workflowBlock.blockId);
    if (!block) return;
    
    // Special handling for decision-router blocks
    if (block.id === 'decision-router') {
      // Open the configuration modal instead
      setConfiguringBlock(workflowBlock);
      setConfigModalOpen(true);
      return;
    }
    
    setTestBlockData({
      id: block.id,
      blockId: block.id,
      name: block.name,
      prompt: block.prompt || '',
      model: block.model || 'gpt-4o-mini'
    });
    setIsTestModalOpen(true);
  };

  const handleSavePrompt = async (newPrompt: string, newModel?: string) => {
    // Update block prompt and model
    try {
      const block = blocks.find(b => b.id === testBlockData.id);
      if (!block) return;
      
      const updatedBlock = { ...block, prompt: newPrompt };
      if (newModel) {
        updatedBlock.model = newModel;
      }
      await api.updateBlock(testBlockData.id, updatedBlock);
      
      // Refresh blocks
      const updatedBlocks = await api.getBlocks();
      setBlocks(updatedBlocks);
    } catch (error) {
      console.error('Failed to save prompt:', error);
    }
  };

  // Function to check if a block can be a connection source
  const canBeConnectionSource = (blockId: string): boolean => {
    return blockId !== 'results-block';
  };

  // Function to check if a block can be a connection target  
  const canBeConnectionTarget = (blockId: string): boolean => {
    return blockId !== 'start-block';
  };

  // Get connection button styling based on state
  const getConnectionButtonStyles = (blockId: string, isActive: boolean): string => {
    if (isActive) {
      return 'bg-red-100 text-red-600 hover:bg-red-200'; // Cancel mode
    }
    if (blockId === 'results-block') {
      return 'bg-gray-100 text-gray-400 cursor-not-allowed';
    }
    return 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200';
  };

  // Calculate connection path between two blocks
  const calculateConnectionPoints = (
    sourcePos: Position, 
    targetPos: Position, 
    isTemp: boolean = false,
    routeInfo?: { sourceBlockId: string; routeId: string }
  ) => {
    // Block dimensions
    const blockWidth = 256;
    
    // Calculate centers with dynamic height
    const sourceCenter = { x: sourcePos.x + blockWidth / 2, y: sourcePos.y + 32 }; // Default 64px / 2
    const targetCenter = { x: targetPos.x + blockWidth / 2, y: targetPos.y + 32 };
    
    // Calculate angle between blocks
    const dx = targetCenter.x - sourceCenter.x;
    const dy = targetCenter.y - sourceCenter.y;
    
    let startPoint = { x: 0, y: 0 };
    let endPoint = { x: 0, y: 0 };
    let sourceSide: number | undefined;
    let targetSide: number | undefined;
    
    // Check if this is a Decision Router connection with a specific route
    if (routeInfo) {
      // Calculate route handle position mathematically instead of querying DOM
      // This ensures consistent positioning even after page refresh
      const sourceBlock = workflow?.blocks.find(b => b.id === routeInfo.sourceBlockId);
      if (sourceBlock) {
        const routes = (sourceBlock as any)?.data?.routes || [];
        const routeIndex = routes.findIndex((r: any) => r.id === routeInfo.routeId);
        
        if (routeIndex !== -1) {
          const totalRoutes = routes.length;
          const spacing = 100 / (totalRoutes + 1);
          const topPosition = spacing * (routeIndex + 1);
          
          // Calculate block height - Decision Router has dynamic height based on routes
          // Base height: 64px + (routes * 40px for each route section)
          let sourceHeight = 64;
          const sourceElement = document.querySelector(`[style*="left: ${sourcePos.x}px"][style*="top: ${sourcePos.y}px"]`);
          if (sourceElement) {
            // DOM is ready, use actual height
            sourceHeight = sourceElement.getBoundingClientRect().height / scale;
          } else {
            // DOM not ready (e.g., after refresh), estimate height
            // Decision Router: base 120px + additional space for routes
            sourceHeight = Math.max(120, 80 + (totalRoutes * 35));
          }
          
          // Calculate the route handle position on the right side of the block
          startPoint = {
            x: sourcePos.x + blockWidth, // Right edge of block
            y: sourcePos.y + (sourceHeight * topPosition / 100) // Position based on route index
          };
          sourceSide = 1; // Force right side for Decision Router
          
        } else {
          // Route not found in routes array - THIS IS THE ROOT CAUSE
          console.error(`❌ CRITICAL: Route ${routeInfo.routeId} not found in Decision Router ${routeInfo.sourceBlockId} routes!`, {
            routeId: routeInfo.routeId,
            availableRoutes: routes.map((r: any) => r.id),
            routesCount: routes.length,
            sourceBlockId: routeInfo.sourceBlockId,
            sourceBlock: sourceBlock,
            sourceBlockData: sourceBlock.data,
            sourceBlockDataKeys: sourceBlock.data ? Object.keys(sourceBlock.data) : [],
            workflowBlocks: workflow?.blocks.map(b => ({ 
              id: b.id, 
              blockId: b.blockId,
              isDecisionRouter: b.blockId === 'decision-router',
              hasData: !!b.data,
              dataKeys: b.data ? Object.keys(b.data) : []
            }))
          });
          // Fall through to default calculation - this will cause all connections to render at same point
          // This is intentional to make the bug visible
        }
      } else {
        // Source block not found - this shouldn't happen, but log it
        console.error(`❌ Source block ${routeInfo.sourceBlockId} not found for route ${routeInfo.routeId}`, {
          availableBlocks: workflow?.blocks.map(b => ({ id: b.id, blockId: b.blockId }))
        });
      }
    }
    
    // If not a route-specific connection (or if route not found), calculate based on angle
    if (startPoint.x === 0 && startPoint.y === 0) {
      // Determine source side based on angle
      const angle = Math.atan2(dy, dx) * 180 / Math.PI;
      if (angle > -45 && angle <= 45) {
        sourceSide = 1; // right side of source
      } else if (angle > 45 && angle <= 135) {
        sourceSide = 2; // bottom of source
      } else if ((angle > 135 && angle <= 180) || (angle >= -180 && angle <= -135)) {
        sourceSide = 3; // left side of source
      } else {
        sourceSide = 0; // top of source
      }
      
      // Get actual block height from DOM for non-decision blocks (account for scale)
      const sourceElement = document.querySelector(`[style*="left: ${sourcePos.x}px"][style*="top: ${sourcePos.y}px"]`);
      const sourceHeight = sourceElement ? sourceElement.getBoundingClientRect().height / scale : 64;
      
      // Source exit point based on determined side
      switch (sourceSide) {
        case 0: // top
          startPoint = { 
            x: sourceCenter.x, 
            y: sourcePos.y 
          };
          break;
        case 1: // right
          startPoint = { 
            x: sourcePos.x + blockWidth, 
            y: sourceCenter.y 
          };
          break;
        case 2: // bottom
          startPoint = { 
            x: sourceCenter.x, 
            y: sourcePos.y + sourceHeight 
          };
          break;
        case 3: // left
          startPoint = { 
            x: sourcePos.x, 
            y: sourceCenter.y 
          };
          break;
      }
    }
    
    // Determine target side if not already set
    if (targetSide === undefined) {
      const angle = Math.atan2(dy, dx) * 180 / Math.PI;
      if (angle > -45 && angle <= 45) {
        targetSide = 3; // left side of target
      } else if (angle > 45 && angle <= 135) {
        targetSide = 0; // top of target
      } else if ((angle > 135 && angle <= 180) || (angle >= -180 && angle <= -135)) {
        targetSide = 1; // right side of target
      } else {
        targetSide = 2; // bottom of target
      }
    }
    
    // Target entry point (measure actual DOM height, account for scale)
    const targetElement = document.querySelector(`[style*="left: ${targetPos.x}px"][style*="top: ${targetPos.y}px"]`);
    const targetHeight = targetElement ? targetElement.getBoundingClientRect().height / scale : 64;
    
    switch (targetSide) {
      case 0: // top
        endPoint = { 
          x: targetCenter.x, 
          y: targetPos.y 
        };
        break;
      case 1: // right
        endPoint = { 
          x: targetPos.x + blockWidth, 
          y: targetCenter.y 
        };
        break;
      case 2: // bottom
        endPoint = { 
          x: targetCenter.x, 
          y: targetPos.y + targetHeight 
        };
        break;
      case 3: // left
        endPoint = { 
          x: targetPos.x, 
          y: targetCenter.y 
        };
        break;
      default: // fallback to left side
        endPoint = { 
          x: targetPos.x, 
          y: targetCenter.y 
        };
        break;
    }
    
    // Calculate control points for the curve
    const distance = Math.sqrt(Math.pow(endPoint.x - startPoint.x, 2) + Math.pow(endPoint.y - startPoint.y, 2));
    const controlPointOffset = Math.min(80, distance * 0.3);
    
    // Direction vectors
    let sourceVector = { x: 0, y: 0 };
    let targetVector = { x: 0, y: 0 };
    
    // Source direction vector
    switch (sourceSide) {
      case 0: // top
        sourceVector = { x: 0, y: -1 };
        break;
      case 1: // right
        sourceVector = { x: 1, y: 0 };
        break;
      case 2: // bottom
        sourceVector = { x: 0, y: 1 };
        break;
      case 3: // left
        sourceVector = { x: -1, y: 0 };
        break;
      default: // fallback to right
        sourceVector = { x: 1, y: 0 };
        break;
    }
    
    // Target direction vector
    switch (targetSide) {
      case 0: // top
        targetVector = { x: 0, y: -1 };
        break;
      case 1: // right
        targetVector = { x: 1, y: 0 };
        break;
      case 2: // bottom
        targetVector = { x: 0, y: 1 };
        // Adjust the endpoint for bottom connections to make arrow more visible
        endPoint.y += 5; 
        break;
      case 3: // left
        targetVector = { x: -1, y: 0 };
        break;
      default: // fallback to left
        targetVector = { x: -1, y: 0 };
        break;
    }
    
    const controlPoint1 = {
      x: startPoint.x + sourceVector.x * controlPointOffset,
      y: startPoint.y + sourceVector.y * controlPointOffset
    };
    
    const controlPoint2 = {
      x: endPoint.x + targetVector.x * controlPointOffset,
      y: endPoint.y + targetVector.y * controlPointOffset
    };
    
    // Calculate path
    const path = `M ${startPoint.x},${startPoint.y} `
      + `C ${controlPoint1.x},${controlPoint1.y} `
      + `${controlPoint2.x},${controlPoint2.y} `
      + `${endPoint.x},${endPoint.y}`;
    
    // Calculate midpoint for direction indicator and delete button
    const midPoint = {
      x: (startPoint.x + endPoint.x) / 2,
      y: (startPoint.y + endPoint.y) / 2
    };
    
    // Determine if the connection is primarily horizontal or vertical
    const isHorizontal = Math.abs(endPoint.x - startPoint.x) > Math.abs(endPoint.y - startPoint.y);
    
    // Determine arrow direction based on the target side
    let arrowDirection = "default";
    if (targetSide === 2) {
      arrowDirection = "vertical-down";
    }
    
    return {
      path,
      startPoint,
      endPoint,
      midPoint,
      directionPoint: midPoint,
      isHorizontal,
      arrowDirection
    };
  };

  return (
    <div className="workflow-editor flex" style={{ height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      <style>{`
      /* Prevent body scroll while in workflow editor */
      body {
        overflow: hidden;
      }
      
      /* Custom scrollbar styles */
        .workflow-sidebar-content::-webkit-scrollbar {
          width: 8px;
        }

        .workflow-sidebar-content::-webkit-scrollbar-track {
          background: #f9fafb;
          border-radius: 4px;
        }

        .workflow-sidebar-content::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 4px;
        }

        .workflow-sidebar-content::-webkit-scrollbar-thumb:hover {
          background-color: #94a3b8;
        }

        /* Firefox scrollbar styles */
        @supports (scrollbar-width: thin) {
          .workflow-sidebar-content {
            scrollbar-width: thin;
            scrollbar-color: #cbd5e1 #f9fafb;
          }
        }

      .process-blocks-container::-webkit-scrollbar {
          width: 8px;
      }

      .process-blocks-container::-webkit-scrollbar-track {
        background: #f9fafb;
          border-radius: 4px;
      }

      .process-blocks-container::-webkit-scrollbar-thumb {
        background-color: #cbd5e1;
        border-radius: 4px;
      }

      .process-blocks-container::-webkit-scrollbar-thumb:hover {
        background-color: #94a3b8;
      }

      /* Firefox scrollbar styles */
      @supports (scrollbar-width: thin) {
        .process-blocks-container {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f9fafb;
        }
      }

      /* Process blocks container styles */
      .process-blocks-container {
        overflow-y: auto;
        overflow-x: hidden;
        height: 100%;
        max-height: calc(100vh - 300px);
        margin-bottom: 1rem;
      }

      /* Results box custom scrollbar styles */
      .workflow-editor div[style*="fontFamily"]::-webkit-scrollbar {
        width: 8px;
      }

      .workflow-editor div[style*="fontFamily"]::-webkit-scrollbar-track {
        background: #f3f4f6;
        border-radius: 4px;
      }

      .workflow-editor div[style*="fontFamily"]::-webkit-scrollbar-thumb {
        background-color: #cbd5e1;
        border-radius: 4px;
        border: 1px solid #f3f4f6;
      }

      .workflow-editor div[style*="fontFamily"]::-webkit-scrollbar-thumb:hover {
        background-color: #94a3b8;
      }

      .workflow-editor div[style*="fontFamily"]::-webkit-scrollbar-corner {
        background: #f3f4f6;
        padding-right: 0.5rem;
        -webkit-overflow-scrolling: touch; /* For smoother scrolling on iOS */
      }

      /* Make scrollbars visible on all browsers */
      .process-blocks-container::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }

      .process-blocks-container::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }

      .process-blocks-container::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
      }

      .process-blocks-container::-webkit-scrollbar-thumb:hover {
        background: #a1a1a1;
      }

      .workflow-canvas {
        flex: 1;
        position: relative;
          overflow: hidden;
        background-color: #f9fafb;
        transition: width 0.2s ease-out;
        height: 100%;
        cursor: grab;
      }

      .workflow-canvas.is-panning {
        cursor: grabbing;
      }

      .workflow-canvas-content {
        position: absolute;
        top: 0;
        left: 0;
          width: 50000px;
          height: 50000px;
        will-change: transform;
        transition: transform 0.05s ease-out;
      }

      /* Disable transitions during panning for better performance */
      .workflow-canvas.is-panning .workflow-canvas-content {
        transition: none;
      }

      /* Scrollbar styles for canvas */
      .workflow-canvas::-webkit-scrollbar {
        width: 10px;
        height: 10px;
      }

      .workflow-canvas::-webkit-scrollbar-track {
        background: #f1f1f1;
      }

      .workflow-canvas::-webkit-scrollbar-thumb {
        background-color: #cbd5e1;
        border-radius: 5px;
        border: 2px solid #f1f1f1;
      }

      .workflow-canvas::-webkit-scrollbar-thumb:hover {
        background-color: #94a3b8;
      }

      .workflow-canvas::-webkit-scrollbar-corner {
        background: #f1f1f1;
      }

      /* Connection line styles */
      .connection-line {
        transition: stroke 0.2s, filter 0.2s;
        will-change: d, transform;
      }

      .connection-line:hover {
        stroke: #4F46E5;
        filter: drop-shadow(0 0 2px rgba(99, 102, 241, 0.6));
      }

      .block-dragging .connection-line,
      .block-dragging .connection-indicator,
      .block-dragging .connection-endpoint {
        transition: none !important;
        will-change: d, transform;
      }

      /* Block styles */
      .workflow-block {
        transform: translate3d(0, 0, 0);
        backface-visibility: hidden;
        transition: all 0.05s ease-out;
        -webkit-font-smoothing: subpixel-antialiased;
      }

      .block-being-dragged {
        z-index: 50 !important;
        transform: translate3d(0, 0, 0);
        will-change: transform;
        transition: none !important;
      }

      /* SVG optimizations */
      .block-dragging svg * {
        vector-effect: non-scaling-stroke;
        transition: none !important;
      }

      svg.optimized-svg {
        shape-rendering: optimizeSpeed;
      }

      /* Flow indicator */
      .flow-indicator {
        will-change: transform;
      }

      /* Optimize performance during interactions */
      .workflow-canvas.is-panning *,
      .workflow-canvas.is-zooming * {
        pointer-events: none;
      }

      .workflow-canvas.is-panning .workflow-block,
      .workflow-canvas.is-zooming .workflow-block {
        transition: none !important;
      }

      /* Hide scrollbar for Firefox but maintain functionality */
      @supports (scrollbar-width: thin) {
        .workflow-sidebar-content,
        .process-blocks-container {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f9fafb;
        }
      }

      /* Connection styles */
      .connection-delete-button {
        opacity: 1;
        z-index: 200;
      }
      
      .connection-delete-button:hover {
        transform: scale(1.1);
        }

        /* Add pulsing animation for chosen route */
        @keyframes pulse {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>

      {/* Sidebar */}
      <div className="workflow-sidebar" style={{ width: '320px', minWidth: '320px', maxWidth: '320px' }}>
        <div className="p-4 border-b border-gray-200 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">Process Blocks</h2>
          <p className="text-xs text-gray-500 mt-1">Drag blocks onto the canvas to build your workflow</p>

          {/* Start text input */}
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Workflow Input Text
            </label>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-md text-xs resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter the starting text for your workflow..."
              rows={3}
              value={startText}
              onChange={(e) => setStartText(e.target.value)}
            />
          </div>

          {/* Execute Workflow Button */}
          <div className="mt-3">
            <button 
              className={`w-full px-4 py-2 rounded-md shadow-md font-medium ${
                isExecuting 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
              onClick={executeWorkflow}
              disabled={isExecuting}
            >
              {isExecuting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Executing...
                </>
              ) : 'Execute Workflow'}
            </button>

            {/* Show execution status if available */}
            {executionStatus && (
              <div className={`mt-2 text-center px-3 py-1 rounded-full text-xs font-medium ${
                executionStatus === 'completed' 
                  ? 'bg-green-100 text-green-800' 
                  : executionStatus === 'failed' 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-blue-100 text-blue-800'
              }`}>
                {executionStatus.charAt(0).toUpperCase() + executionStatus.slice(1)}
              </div>
            )}
          </div>

          {/* Search Filter */}
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search Blocks</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Search by name, description, or category..."
              value={blockSearchText}
              onChange={(e) => setBlockSearchText(e.target.value)}
            />
          </div>
        
          {/* Category Filter */}
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-700 mb-2">Filter by Category</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedCategory === null
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {categories.map((category) => (
                <button 
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
              </div>
          </div>
          
        <div className="flex-1 overflow-hidden flex flex-col workflow-sidebar-content">
          {/* Scrollable container for process blocks */}
          <div 
            className="process-blocks-container"
            style={{
              height: 'calc(100vh - 460px)', // Fill remaining height
              overflowY: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              background: '#f9fafb',
              paddingBottom: '15px' // Extra padding at container level
            }}
          >
            <div className="space-y-3 p-3 pb-6">
              {getFilteredBlocks().map((block) => (
                <div
                  key={block.id}
                  draggable
                  onDragStart={() => handleDragStart(block)}
                  onDragEnd={handleDragEnd}
                  className={`p-3 rounded-lg shadow cursor-move hover:shadow-md transition-shadow duration-200 ${getSidebarBlockStyle(block.id)}`}
                  style={{ position: 'relative' }}
                >
                  {/* Beta badge for Decision Router */}
                  {block.id === 'decision-router' && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: '#f97316',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      zIndex: 10
                    }}>
                      BETA
                    </div>
                  )}
                  <div className="p-3" style={{ minHeight: '80px' }}>
                    <h3 className="text-lg font-medium text-gray-900">{block.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">{block.description}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryBadgeStyle(block.id)}`}>
                        {block.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {/* Spacer element to ensure last block is fully visible when scrolling */}
              <div style={{ height: '50px' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed zoom controls - positioned in the viewport */}
      <div className="fixed top-20 right-6 z-50 flex flex-col bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <button
          className="p-2 hover:bg-gray-100 flex items-center justify-center border-b border-gray-200"
          onClick={zoomIn}
          title="Zoom in"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
        </button>
        <div className="px-2 py-1 text-center text-sm font-medium border-b border-gray-200">
          {Math.round(scale * 100)}%
        </div>
        <button
          className="p-2 hover:bg-gray-100 flex items-center justify-center border-b border-gray-200"
          onClick={zoomOut}
          title="Zoom out"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </button>
        <button
          className="p-2 hover:bg-gray-100 flex items-center justify-center border-b border-gray-200"
          onClick={resetZoom}
          title="Reset zoom"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
          </svg>
        </button>
        <button
          className="p-2 hover:bg-gray-100 flex items-center justify-center"
          onClick={autoLayoutBlocks}
          title="Auto-layout blocks"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        </button>
      </div>

      {/* Canvas Container */}
      <div 
        className="workflow-canvas"
        ref={containerRef}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleCanvasClick}
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#f5f5f5'
        }}
      >
        <div
          className="workflow-canvas-content"
          style={{
            transform: `scale(${scale}) translate3d(0, 0, 0)`,
            transformOrigin: '0 0',
            backgroundImage: 'radial-gradient(circle, #b3b3b3 1px, transparent 1px)',
            backgroundSize: `${20 / scale}px ${20 / scale}px`, // Scale the grid with zoom
            position: 'relative',
            left: `${panPosition.x}px`,
            top: `${panPosition.y}px`,
            willChange: 'transform, left, top',
          }}
        >
          {/* Connection Mode Indicator */}
          {isConnecting && (
            <div className="absolute top-4 left-4 z-10 bg-blue-100 rounded-md py-1 px-3 shadow-md text-sm text-blue-800 flex items-center">
              <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Connection Mode: Click on a block to connect or anywhere to cancel
            </div>
          )}

          {/* Saving indicator */}
          {isSaving && (
            <div className="absolute top-4 left-4 z-10 bg-white rounded-md py-1 px-3 shadow-md text-sm text-gray-600 flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </div>
          )}

          {/* Workflow Summary Panel */}
          {workflow && (
          <div className="absolute bottom-4 right-4 bg-white p-3 rounded-md shadow-md z-10 max-w-xs">
            <h3 className="font-medium text-sm text-gray-900 mb-1">Workflow Summary</h3>
            <div className="text-xs text-gray-600 space-y-1">
              <p><span className="font-medium">Name:</span> {workflow.name}</p>
              <p><span className="font-medium">Blocks:</span> {workflow.blocks.length}</p>
              <p><span className="font-medium">Connections:</span> {workflow.connections.length}</p>
            </div>
          </div>
          )}

          {/* Connection delete buttons - rendered separately as HTML elements */}
          {workflow?.connections.map((connection) => {
            const sourceBlock = workflow.blocks.find(b => b.id === connection.sourceId);
            const targetBlock = workflow.blocks.find(b => b.id === connection.targetId);
            
            if (!sourceBlock || !targetBlock || !sourceBlock.position || !targetBlock.position) return null;
            
            // Pass route info if this is a Decision Router connection
            const routeInfo = connection.routeId ? {
              sourceBlockId: connection.sourceId,
              routeId: connection.routeId
            } : undefined;
            
            const connDetails = calculateConnectionPoints(
              sourceBlock.position, 
              targetBlock.position, 
              false, 
              routeInfo
            );
            
            // Position the delete button at the midpoint of the connection - using a larger size
            const hitboxStyle = {
              position: 'absolute' as 'absolute',
              left: `${connDetails.directionPoint.x - 20}px`,
              top: `${connDetails.directionPoint.y - 20}px`,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              cursor: 'pointer',
              zIndex: 300,
              transform: `scale(${1/scale})`,
              transformOrigin: 'center center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              touchAction: 'none' as 'none',
              pointerEvents: 'auto' as 'auto',
              userSelect: 'none' as 'none',
            };
            
            // The visible button
            const buttonStyle = {
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: 'white',
              border: '2px solid #dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              transition: 'all 0.1s ease-in-out',
              pointerEvents: 'auto' as 'auto',
            };
            
            // This X icon will be displayed in the center of the circle
            const xStyle = {
              color: '#dc2626',
              fontWeight: 'bold' as 'bold',
              fontSize: '14px',
              lineHeight: '1',
              userSelect: 'none' as 'none',
              pointerEvents: 'none' as 'none',
            };
            
            return (
              <div
                key={`delete-hitbox-${connection.id}-${forceUpdate}`}
                style={hitboxStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleConnectionDelete(connection.id);
                }}
                title="Delete connection"
                className="delete-button-hitbox"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleConnectionDelete(connection.id);
                  }
                }}
                role="button"
                aria-label="Delete connection"
              >
                <div 
                  className="connection-delete-button"
                  style={buttonStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee2e2';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.4)';
                    e.currentTarget.style.border = '2px solid #b91c1c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
                    e.currentTarget.style.border = '2px solid #dc2626';
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={xStyle}>
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            );
          })}

          {/* Draw connections */}
          {workflow?.connections.map((connection) => {
            const sourceBlock = workflow.blocks.find(b => b.id === connection.sourceId);
            const targetBlock = workflow.blocks.find(b => b.id === connection.targetId);
            
            if (!sourceBlock || !targetBlock || !sourceBlock.position || !targetBlock.position) return null;
            
            // Pass route info if this connection has a routeId (from Decision Router)
            const routeInfo = connection.routeId ? {
              sourceBlockId: connection.sourceId,
              routeId: connection.routeId
            } : undefined;
            
            const connDetails = calculateConnectionPoints(
              sourceBlock.position, 
              targetBlock.position, 
              false, 
              routeInfo
            );
            
            // Get route color if this is a Decision Router connection
            let routeColor = "#6366f1"; // Default indigo
            let isChosenRoute = false; // Track if this is the route that was taken
            
            if (routeInfo) {
              const routes = (sourceBlock as any)?.data?.routes;
              const route = routes?.find((r: any) => r.id === connection.routeId);
              if (route?.color) {
                routeColor = route.color;
              }
              
              // Check if this route was taken in the execution
              if (executionStatus === 'completed' && blockData[connection.sourceId]) {
                try {
                  const sourceOutput = blockData[connection.sourceId].output;
                  const decisionOutput = typeof sourceOutput === 'string' ? JSON.parse(sourceOutput) : sourceOutput;
                  isChosenRoute = decisionOutput.chosenRoute === connection.routeId;
                } catch (e) {
                  // Ignore parsing errors
                }
              }
            }
            
            return (
              <svg 
                key={`${connection.id}-${forceUpdate}`} 
                className="absolute top-0 left-0 w-full h-full overflow-visible" 
                style={{ zIndex: 10 }}
              >
                <defs>
                  {/* Regular arrowhead - Sharp triangle */}
                  <marker
                    id={`arrowhead-${connection.id}`}
                    viewBox="0 0 10 10"
                    markerWidth="8"
                    markerHeight="8"
                    refX="9"
                    refY="5"
                    orient="auto"
                    markerUnits="strokeWidth">
                    <path d="M0,0 L0,10 L10,5 z" fill={routeColor} />
                  </marker>
                  
                  {/* Downward-oriented arrowhead - Sharp triangle */}
                  <marker
                    id={`arrowhead-down-${connection.id}`}
                    viewBox="0 0 10 10"
                    markerWidth="8"  
                    markerHeight="8"  
                    refX="5"  
                    refY="9"  
                    orient="auto"
                    markerUnits="strokeWidth">
                    <path d="M0,0 L10,0 L5,10 z" fill={routeColor} />
                  </marker>
                </defs>
                
                {/* Connection backdrop (shadow effect) */}
                <path
                  d={connDetails.path}
                  fill="none"
                  stroke="#000"
                  strokeWidth={isChosenRoute ? "8" : "6"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="none"
                  opacity={isChosenRoute ? "0.15" : (routeInfo && !isChosenRoute && executionStatus === 'completed' ? "0.03" : "0.1")}
                />
                
                {/* Main connection line with arrow marker */}
                <path
                  d={connDetails.path}
                  fill="none"
                  stroke={routeColor}
                  strokeWidth={isChosenRoute ? "5" : "3"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="none"
                  opacity={routeInfo && !isChosenRoute && executionStatus === 'completed' ? 0.2 : 1}
                  markerEnd={connDetails.arrowDirection === "vertical-down" ? 
                    `url(#arrowhead-down-${connection.id})` : 
                    `url(#arrowhead-${connection.id})`}
                />
                
                {/* Highlight pulse effect for chosen route */}
                {isChosenRoute && (
                  <path
                    d={connDetails.path}
                    fill="none"
                    stroke={routeColor}
                    strokeWidth="7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="none"
                    opacity="0.3"
                    style={{
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  />
                )}
                
                {/* Start point - only show for route connections */}
                {routeInfo && connDetails.startPoint && !isNaN(connDetails.startPoint.x) && !isNaN(connDetails.startPoint.y) && (
                  <circle
                    cx={connDetails.startPoint.x}
                    cy={connDetails.startPoint.y}
                    r={isChosenRoute ? "6" : "4"}
                    fill={routeColor}
                    opacity={isChosenRoute ? "1" : (routeInfo && !isChosenRoute && executionStatus === 'completed' ? "0.3" : "0.9")}
                    stroke="white"
                    strokeWidth={isChosenRoute ? "2" : "1.5"}
                  />
                )}
                
                {/* End point */}
                {connDetails.endPoint && !isNaN(connDetails.endPoint.x) && !isNaN(connDetails.endPoint.y) && (
                  <circle
                    cx={connDetails.endPoint.x}
                    cy={connDetails.endPoint.y}
                    r={isChosenRoute ? "5" : "3"}
                    fill={routeColor}
                    opacity={isChosenRoute ? "0.9" : (routeInfo && !isChosenRoute && executionStatus === 'completed' ? "0.2" : "0.6")}
                  />
                )}

                {/* For bottom connections, add a visible endpoint circle regardless of the marker */}
                {connDetails.arrowDirection === "vertical-down" && connDetails.endPoint && !isNaN(connDetails.endPoint.x) && !isNaN(connDetails.endPoint.y) && (
                  <circle
                    cx={connDetails.endPoint.x}
                    cy={connDetails.endPoint.y}
                    r={isChosenRoute ? "6" : "5"}
                    fill={routeColor}
                    opacity={isChosenRoute ? "1" : (routeInfo && !isChosenRoute && executionStatus === 'completed' ? "0.3" : "0.8")}
                  />
                )}
              </svg>
            );
          })}

          {/* Temporary connection line when connecting blocks */}
          {isConnecting && tempConnection && (
            <svg className="absolute top-0 left-0 w-full h-full overflow-visible pointer-events-none" style={{ zIndex: 5 }}>
              <defs>
                {/* Regular temp arrowhead - Sharp triangle */}
                <marker
                  id="temp-arrowhead"
                  viewBox="0 0 10 10"
                  markerWidth="8"
                  markerHeight="8"
                  refX="9"
                  refY="5"
                  orient="auto"
                  markerUnits="strokeWidth">
                  <path d="M0,0 L0,10 L10,5 z" fill="#6366f1" />
                </marker>
                
                {/* Downward-oriented temp arrowhead - Sharp triangle */}
                <marker
                  id="temp-arrowhead-down"
                  viewBox="0 0 10 10"
                  markerWidth="8"
                  markerHeight="8"
                  refX="5"
                  refY="9"
                  orient="auto"
                  markerUnits="strokeWidth">
                  <path d="M0,0 L10,0 L5,10 z" fill="#6366f1" />
                </marker>
              </defs>
              
              {/* Calculate temporary connection points */}
              {(() => {
                if (!sourceBlock || !sourceBlock.position) return null;
                
                const connDetails = calculateConnectionPoints(
                  sourceBlock.position,
                  { x: tempConnection.end.x, y: tempConnection.end.y },
                  true
                );
                
                return (
                  <>
                    {/* Connection backdrop */}
                    <path
                      d={connDetails.path}
                      fill="none"
                      stroke="#000"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray="4"
                      opacity="0.1"
                    />
                    
                    {/* Main connection line */}
                    <path
                      d={connDetails.path}
                      fill="none"
                      stroke="#6366f1"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray="4"
                      markerEnd={connDetails.arrowDirection === "vertical-down" ?
                        "url(#temp-arrowhead-down)" : "url(#temp-arrowhead)"}
                    />
                    
                    {/* Start point */}
                    {connDetails.startPoint && !isNaN(connDetails.startPoint.x) && !isNaN(connDetails.startPoint.y) && (
                      <circle
                        cx={connDetails.startPoint.x}
                        cy={connDetails.startPoint.y}
                        r="3"
                        fill="#6366f1"
                        opacity="0.6"
                      />
                    )}
                    
                    {/* End point */}
                    {connDetails.endPoint && !isNaN(connDetails.endPoint.x) && !isNaN(connDetails.endPoint.y) && (
                      <circle
                        cx={connDetails.endPoint.x}
                        cy={connDetails.endPoint.y}
                        r="3"
                        fill="#6366f1"
                        opacity="0.6"
                      />
                    )}
                    
                    {/* For bottom connections in temporary mode, add a more visible endpoint circle */}
                    {connDetails.arrowDirection === "vertical-down" && connDetails.endPoint && !isNaN(connDetails.endPoint.x) && !isNaN(connDetails.endPoint.y) && (
                      <circle
                        cx={connDetails.endPoint.x}
                        cy={connDetails.endPoint.y}
                        r="5"
                        fill="#6366f1"
                        opacity="0.8"
                      />
                    )}
                  </>
                );
              })()}
            </svg>
          )}

          {/* Render blocks */}
          {workflow?.blocks.map((workflowBlock) => {
            const block = blocks.find(b => b.id === workflowBlock.blockId);
            if (!block || !workflowBlock.position) return null;

            return (
              <div
                key={workflowBlock.id}
                className={`absolute rounded-lg shadow-lg p-4 w-64 workflow-block ${
                  isDragging && draggedBlockId === workflowBlock.id 
                    ? 'shadow-2xl ring-2 ring-blue-400 z-50 block-being-dragged'
                    : isConnecting && sourceBlock?.id !== workflowBlock.id && canBeConnectionTarget(block.id)
                      ? 'hover:shadow-xl z-30 ring-2 ring-indigo-200 hover:ring-indigo-400' 
                      : 'hover:shadow-xl z-30'
                } transition-all duration-150 select-none ${getBlockColor(block.id)}`}
                style={{
                  left: workflowBlock.position.x,
                  top: workflowBlock.position.y,
                  userSelect: 'none',
                  cursor: isDragging && draggedBlockId === workflowBlock.id ? 'grabbing' : 'grab',
                  transform: isDragging && draggedBlockId === workflowBlock.id ? 'translate3d(0,0,0)' : '',
                }}
                onClick={(e) => handleBlockClick(e, workflowBlock)}
                onDoubleClick={(e) => handleBlockDoubleClick(e, workflowBlock)}
                onMouseDown={(e) => handleBlockMouseDown(e, workflowBlock.id, workflowBlock.position)}
                onMouseEnter={() => setHoveredBlockId(workflowBlock.id)}
                onMouseLeave={() => setHoveredBlockId(null)}
              >
                {/* Beta badge for Decision Router on canvas */}
                {block.id === 'decision-router' && (
                  <div style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    backgroundColor: '#f97316',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    zIndex: 20
                  }}>
                    BETA
                  </div>
                )}
                
                {/* Special rendering for Decision Router blocks with multiple routes */}
                {block.id === 'decision-router' && (workflowBlock as any).data?.routes ? (
                  <>
                    {/* Input connection point (left side) */}
                    <div 
                      className={`absolute w-4 h-4 bg-green-500 border-2 border-white rounded-full shadow-sm
                        left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10
                        ${isConnecting && sourceBlock?.id !== workflowBlock.id ? 'opacity-100 ring-2 ring-green-300' : 'opacity-0'} 
                        ${hoveredBlockId === workflowBlock.id ? 'opacity-100' : ''} 
                        transition-opacity duration-200 hover:opacity-100`}
                      title="Connect to this block"
                    />
                    
                    {/* Route output connection points (right side) - stacked vertically */}
                    {((workflowBlock as any).data?.routes || []).map((route: any, index: number) => {
                      const totalRoutes = ((workflowBlock as any).data?.routes || []).length;
                      const spacing = 100 / (totalRoutes + 1);
                      const topPosition = spacing * (index + 1);
                      
                      return (
                        <div
                          key={route.id}
                          className="absolute right-0 z-10"
                          style={{
                            top: `${topPosition}%`,
                            transform: 'translateY(-50%)',
                          }}
                        >
                          {/* Route label */}
                          <div
                            className={`absolute right-6 px-2 py-1 rounded text-xs font-medium whitespace-nowrap shadow-sm
                              ${hoveredBlockId === workflowBlock.id ? 'opacity-100' : 'opacity-0'}
                              transition-opacity duration-200`}
                            style={{
                              backgroundColor: route.color || '#4CAF50',
                              color: 'white',
                              transform: 'translateY(-50%)',
                              top: '50%',
                            }}
                          >
                            {route.name}
                          </div>
                          
                          {/* Route connection handle */}
                          <div
                            data-route-handle="true"
                            data-block-id={workflowBlock.id}
                            data-route-id={route.id}
                            className={`w-4 h-4 border-2 border-white rounded-full shadow-sm cursor-pointer transform translate-x-1/2
                              ${hoveredBlockId === workflowBlock.id ? 'opacity-100' : 'opacity-70'}
                              hover:opacity-100 hover:scale-125 transition-all duration-200
                              ${isConnecting && sourceBlock?.id === workflowBlock.id && selectedRouteId === route.id ? 'ring-2 ring-white animate-pulse scale-125' : ''}`}
                            style={{
                              backgroundColor: route.color || '#4CAF50',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Initiate connection from this specific route
                              handleConnectionButtonClick(e, workflowBlock, route.id, index, totalRoutes);
                            }}
                            title={`Connect from: ${route.name}`}
                          />
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {/* Standard connection points for non-decision blocks */}
                {/* Connection source point (right side) */}
                {canBeConnectionSource(block.id) && (
                  <div 
                    className={`absolute w-4 h-4 bg-indigo-500 border-2 border-white rounded-full shadow-sm
                      right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 z-10
                      ${isConnecting && sourceBlock?.id === workflowBlock.id ? 'ring-2 ring-indigo-300 animate-pulse' : ''}
                      ${hoveredBlockId === workflowBlock.id && !isConnecting ? 'opacity-100' : 'opacity-0'} 
                      transition-opacity duration-200 hover:opacity-100`}
                    title="Drag from here to connect"
                  />
                )}
                
                {/* Connection target point (left side) */}
                {canBeConnectionTarget(block.id) && (
                  <div 
                    className={`absolute w-4 h-4 bg-green-500 border-2 border-white rounded-full shadow-sm
                      left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10
                      ${isConnecting && sourceBlock?.id !== workflowBlock.id ? 'opacity-100 ring-2 ring-green-300' : 'opacity-0'} 
                      ${hoveredBlockId === workflowBlock.id ? 'opacity-100' : ''} 
                      transition-opacity duration-200 hover:opacity-100`}
                    title="Connect to this block"
                  />
                    )}
                  </>
                )}
                
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                  <h3 className="font-medium">{block.name}</h3>
                    {/* Show chosen route badge for decision blocks after execution */}
                    {block.id === 'decision-router' && executionStatus === 'completed' && blockData[workflowBlock.id] && (() => {
                      try {
                        const output = blockData[workflowBlock.id].output;
                        const decisionOutput = typeof output === 'string' ? JSON.parse(output) : output;
                        if (decisionOutput && decisionOutput.routeName) {
                          const routes = (workflowBlock as any).data?.routes || [];
                          const chosenRoute = routes.find((r: any) => r.id === decisionOutput.chosenRoute || r.name === decisionOutput.routeName);
                          if (chosenRoute) {
                            return (
                              <span 
                                className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full border-2 shadow-sm"
                                style={{
                                  backgroundColor: chosenRoute.color + '30',
                                  borderColor: chosenRoute.color,
                                  color: chosenRoute.color
                                }}
                                title="Route taken"
                              >
                                ➜ {decisionOutput.routeName}
                              </span>
                            );
                          }
                        }
                      } catch (e) {
                        // Ignore parsing errors
                      }
                      return null;
                    })()}
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Only show connection button for blocks that can have connections */}
                    {/* Decision Router uses individual route handles, so don't show the link icon */}
                    {block.id !== 'results-block' && block.id !== 'decision-router' && (
                      <div 
                        className={`w-7 h-7 flex items-center justify-center rounded-full ${getConnectionButtonStyles(block.id, isConnecting && sourceBlock?.id === workflowBlock.id)} cursor-pointer transition-colors shadow-sm hover:shadow`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConnectionButtonClick(e, workflowBlock);
                        }}
                        title={isConnecting && sourceBlock?.id === workflowBlock.id ? "Cancel connection" : "Create connection from this block"}
                      >
                        {isConnecting && sourceBlock?.id === workflowBlock.id ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7h3a5 5 0 015 5 5 5 0 01-5 5h-3m-6 0H6a5 5 0 01-5-5 5 5 0 015-5h3" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h8" />
                          </svg>
                        )}
                      </div>
                    )}
                    
                    {/* Delete button (only shows on hover) */}
                    <div 
                      className={`w-6 h-6 flex items-center justify-center rounded-full bg-white text-gray-400 hover:bg-red-100 hover:text-red-500 cursor-pointer ${hoveredBlockId === workflowBlock.id ? 'opacity-100' : 'opacity-0'} transition-opacity`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete block "${block.name}"?`)) {
                          handleBlockDelete(workflowBlock.id);
                        }
                      }}
                      title="Delete block"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Render block content based on type */}
                {renderBlockContent(block, workflowBlock)}
              </div>
            );
          })}
        </div>
      </div>

      {/* Test Modal */}
      <PromptTestModal
        isOpen={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        blockId={testBlockData.id}
        blockName={testBlockData.name}
        initialPrompt={testBlockData.prompt}
        defaultModel={testBlockData.model}
        onSavePrompt={handleSavePrompt}
      />

      {/* Decision Block Configuration Modal */}
      {configModalOpen && configuringBlock && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          onClick={(e) => {
            // Close modal only if clicking the backdrop
            if (e.target === e.currentTarget) {
              setConfigModalOpen(false);
              setConfiguringBlock(null);
            }
          }}
        >
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => {
                setConfigModalOpen(false);
                setConfiguringBlock(null);
              }}
            />

            {/* Modal panel */}
            <div 
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full relative z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Configure Decision Router</h3>
                  <button
                    onClick={() => {
                      setConfigModalOpen(false);
                      setConfiguringBlock(null);
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <DecisionBlockConfigPanel
                  config={(configuringBlock as any).data || {}}
                  onChange={handleSaveDecisionConfig}
                />
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => {
                    setConfigModalOpen(false);
                    setConfiguringBlock(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowEditor;
